import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const rotatingWords = ['Direct bij fabrieken.', 'Zonder gedoe.', 'Bespaar 30–80%.'];

const HERO_IMG =
  'https://public.readdy.ai/ai/img_res/151b5cd543e520202c9dc0d3b680c992.jpg';

export default function HeroSection() {
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((i) => (i + 1) % rotatingWords.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* HD Warehouse achtergrond foto */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="Groot magazijn logistiek centrum DUC Direct uit China"
          className="w-full h-full object-cover object-center"
          fetchPriority="high"
          loading="eager"
          decoding="async"
        />
        {/* Zwarte overlay 70% — tekst goed leesbaar */}
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/55 via-transparent to-transparent hidden md:block" />
      </div>

      {/* Subtiele glow — alleen desktop */}
      <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-[#3B9EE8]/5 rounded-full blur-3xl pointer-events-none hidden lg:block" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-6 lg:px-10 pt-[100px] sm:pt-[116px] lg:pt-[144px] pb-12 md:pb-20">
        <div className="max-w-2xl">

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 backdrop-blur-sm rounded-full px-3 py-1.5 mb-5"
          >
            <span className="w-1.5 h-1.5 bg-[#3B9EE8] rounded-full animate-pulse flex-shrink-0" />
            <span className="text-[#3B9EE8] text-xs font-500 whitespace-nowrap">Binnen 24 uur reactie &bull; Gratis &amp; vrijblijvend</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="font-heading font-800 text-white leading-tight mb-4"
            style={{ fontSize: 'clamp(1.75rem, 5vw, 3.5rem)', lineHeight: '1.1' }}
          >
            Koop direct bij fabrikanten<br className="hidden sm:block" /> in China
            <br />
            <span className="text-[#3B9EE8]">zonder verrassingen</span>
          </motion.h1>

          {/* Rotating words */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.28 }}
            className="flex items-center gap-3 mb-4"
          >
            <div className="w-5 h-0.5 bg-[#3B9EE8] flex-shrink-0" />
            <span className="relative overflow-hidden h-6 flex items-center">
              <motion.span
                key={wordIndex}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="block text-[#3B9EE8] font-600 text-sm md:text-base"
              >
                {rotatingWords[wordIndex]}
              </motion.span>
            </span>
          </motion.div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="text-white/65 text-sm md:text-base leading-relaxed mb-6 max-w-lg"
          >
            DUC regelt sourcing, productie en DDP levering — van inkoop tot aftercare, volledig geregeld.
          </motion.p>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.42 }}
            className="flex flex-wrap gap-3 mb-5"
          >
            <button
              onClick={() => scrollTo('#contact')}
              className="group flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-5 py-2.5 md:px-6 md:py-3 rounded-lg font-700 text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line" />
              Gratis prijscheck aanvragen
              <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <a
              href="https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China."
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-4 py-2.5 md:px-5 md:py-3 rounded-lg font-500 text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base" />
              WhatsApp
            </a>
          </motion.div>

          {/* Trust badges — 3 items, compact */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="flex flex-wrap items-center gap-3 mb-5"
          >
            {[
              { icon: 'ri-time-line', text: 'Binnen 24 uur' },
              { icon: 'ri-shield-check-line', text: 'Geen verplichtingen' },
              { icon: 'ri-gift-line', text: 'Gratis & vrijblijvend' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-1.5">
                <i className={`${item.icon} text-[#3B9EE8] text-xs`} />
                <span className="text-white/55 text-xs">{item.text}</span>
              </div>
            ))}
          </motion.div>

          {/* Stats bar — 2x2 grid op mobiel, row op desktop */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid grid-cols-2 sm:flex sm:items-center gap-4 sm:gap-8 pt-5 border-t border-white/12"
          >
            {[
              { value: '30–80%', label: 'Besparing', icon: 'ri-money-euro-circle-line' },
              { value: 'DDP', label: 'All-in levering', icon: 'ri-truck-line' },
              { value: '24u', label: 'Reactietijd', icon: 'ri-time-line' },
              { value: '100%', label: 'Transparant', icon: 'ri-shield-check-line' },
            ].map((stat) => (
              <div key={stat.value} className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#3B9EE8]/15 flex-shrink-0">
                  <i className={`${stat.icon} text-[#3B9EE8] text-xs`} />
                </div>
                <div className="flex flex-col">
                  <span className="font-heading font-800 text-white text-base sm:text-xl leading-none">{stat.value}</span>
                  <span className="text-white/40 text-xs mt-0.5 whitespace-nowrap">{stat.label}</span>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="absolute bottom-5 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center"
      >
        <motion.i
          animate={{ y: [0, 5, 0] }}
          transition={{ repeat: Infinity, duration: 1.4 }}
          className="ri-arrow-down-line text-white/30 text-sm"
        />
      </motion.div>
    </section>
  );
}
